"use client";

// The browser's one way to call the platform API.
//
// **Rewritten for the cookie session.** It used to mint a short-lived Okta bearer from
// `/api/platform-token` and carry it in a JS closure (docs/DECISIONS.md G50). The credential is
// now an `HttpOnly` cookie the browser attaches itself, so the token cache, the
// refresh-before-expiry margin and the mint-coalescing are all gone -- they existed only to
// manage a token JavaScript could see.
//
// What that buys is the thing G50 named as its own tradeoff: *"an XSS in the app can now
// exfiltrate a ~1-hour Okta access token."* It could, in fact, do better than that -- any script
// on the origin could simply call the mint endpoint itself. There is no mint endpoint now, and
// no script can read the cookie.
//
// Requests still go through this one function rather than raw `fetch`, for the reason the
// original gives: whichever component notices a dead session first tells every other component
// through one browser-wide event, instead of each screen tracking its own "am I signed out yet?"

/** Broadcast when the session is genuinely gone -- after a refresh has already been tried. */
export const SESSION_EXPIRED_EVENT = "platform:session-expired";

// Same-origin. The Cloudflare Worker proxies `/api/*` to API Gateway and strips the prefix, so
// the browser never makes a cross-origin request and the cookie needs no `SameSite=None`.
//
// A constant rather than an environment variable, matching the same decision on the server side
// (`auth.app.API_PATH_PREFIX`): the prefix is a property of the Worker's routing, which lives in
// this repo, and two values that must agree is one more thing to get out of step than one value
// and a documented convention.
const BASE = "/api";

// **Required on every state-changing call, and it is a CSRF control rather than a convention.**
// The session cookie is `SameSite=Lax`, which already withholds it from cross-site POSTs. This
// closes the rest: a cross-site form cannot set a custom header without a preflight, and the API
// answers no preflight for a foreign origin. It separately makes Cloudflare Access return 401
// rather than an opaque 302 when its session expires, if Access is ever switched on.
const XHR_HEADER = { "X-Requested-With": "XMLHttpRequest" } as const;

/** Which of the two `send`s a report came from. */
type AuthPhase = "initial" | "retry";

/**
 * What the auth path reports about itself.
 *
 * The refresh-and-retry below is invisible when it works, which is the whole point of it -- but
 * that also makes it invisible when it *doesn't*. RUM records the `POST /v1/auth/refresh`
 * resource only once the request reaches the network, so an absent refresh resource cannot
 * distinguish "the refresh was never attempted" from "it was attempted and died before the
 * wire". Those two have opposite fixes, and no telemetry from outside this function can tell
 * them apart -- which is why each branch in here now reports itself.
 *
 * The reporter is **injected rather than imported**, for two reasons. This module is the one
 * thing every API caller goes through, so a browser SDK imported here would sit in front of
 * every request in the app; and `apps/web/tests/platform-fetch.test.mjs` imports this file
 * straight into plain Node, where `@datadog/browser-rum` has no business being resolved.
 * `src/components/datadog-rum.tsx` installs the real reporter next to `datadogRum.init`, and
 * anything that installs none pays one no-op call per rejected credential.
 */
export type AuthTelemetryEvent =
  | {
      step: "credential-rejected";
      path: string;
      phase: AuthPhase;
      status: number;
      reason: "401" | "authorizer-403" | "unreadable-403";
    }
  | { step: "csrf-403"; path: string; phase: AuthPhase }
  | { step: "refresh-start"; path: string; coalesced: boolean }
  | { step: "refresh-settled"; ok: boolean; status: number | null; error: string | null }
  | { step: "retry-recovered"; path: string; status: number }
  | { step: "session-expired"; path: string; after: "refresh-failed" | "retry-rejected" };

let reporter: ((event: AuthTelemetryEvent) => void) | null = null;

/** Installed once, from `src/components/datadog-rum.tsx`. `null` restores the no-op. */
export function setAuthTelemetryReporter(
  report: ((event: AuthTelemetryEvent) => void) | null,
): void {
  reporter = report;
}

function report(event: AuthTelemetryEvent): void {
  try {
    reporter?.(event);
  } catch {
    // Deliberately swallowed, in the same spirit as `purgeLocalState` below: this sits directly
    // in the path of every API call the app makes, and a reporting failure must never be allowed
    // to become a request failure.
  }
}

/**
 * `fetch` rejects with a bare `TypeError` on a network failure and with an `AbortError` when the
 * page navigates out from under an in-flight request. Telling those two apart is most of the
 * reason this instrumentation exists, so keep the name *and* the message.
 */
function errorLabel(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`.slice(0, 200);
  return String(error).slice(0, 200);
}

// **Coalescing survived the rewrite, for a different reason than it had.** It used to stop a
// burst of callers each minting a token. It now stops them each calling `/v1/auth/refresh`: the
// token broker takes a lease and answers 503 on contention, so five components hitting a
// simultaneous 401 -- which is exactly what happens when an access cookie expires while a page
// is open -- would turn one recoverable expiry into four spurious failures.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(path: string): Promise<boolean> {
  // Reported *before* the coalescing check, so a burst shows up as several `refresh-start`s
  // behind one `refresh-settled`. That shape is what demonstrates the coalescing is working --
  // and it is also the shape that would expose a `refreshInFlight` stuck across calls, if one
  // ever survived its `finally`.
  report({ step: "refresh-start", path, coalesced: refreshInFlight !== null });
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, {
        method: "POST",
        credentials: "same-origin",
        headers: XHR_HEADER,
      });
      report({ step: "refresh-settled", ok: res.ok, status: res.status, error: null });
      return res.ok;
    } catch (error) {
      // A network failure is not an expired session. Reporting it as one would sign the user
      // out over a dropped connection they would otherwise not notice.
      //
      // This is the branch RUM cannot see: a `fetch` that never reaches the network leaves no
      // resource event behind, so an expiry that dies in here is indistinguishable in RUM from
      // one where the refresh was never attempted at all.
      report({ step: "refresh-settled", ok: false, status: null, error: errorLabel(error) });
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function sessionExpiredResponse(): Response {
  return new Response(
    JSON.stringify({
      detail: "Your session has expired. Please sign in again.",
      code: "session_expired",
    }),
    { status: 401, headers: { "Content-Type": "application/json" } },
  );
}

function send(path: string, init: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
    ...XHR_HEADER,
  };
  // Only when there is a body -- unlike the old server-side `callPlatformApi`, which set this
  // unconditionally even on a bodyless GET or DELETE.
  if (init.body !== undefined && headers["Content-Type"] === undefined) {
    headers["Content-Type"] = "application/json";
  }
  return fetch(`${BASE}${path}`, { ...init, headers, credentials: "same-origin" });
}

/** Whether a response means "the credential was rejected" rather than "the route said no". */
async function credentialRejected(
  res: Response,
  path: string,
  phase: AuthPhase,
): Promise<boolean> {
  if (res.status === 401) {
    report({ step: "credential-rejected", path, phase, status: 401, reason: "401" });
    return true;
  }
  // Nothing is reported for the overwhelmingly common case. A 200 or a by-convention 404 must
  // not cost an action per request.
  if (res.status !== 403) return false;

  // The CSRF guard is the only 403 this platform raises itself. Reading the body to tell them
  // apart is safe: it is cloned first, so the caller still gets an unread stream if this turns
  // out not to be a credential problem.
  try {
    const body = await res.clone().json();
    if (body?.type === "csrf-check-failed") {
      report({ step: "csrf-403", path, phase });
      return false;
    }
    report({ step: "credential-rejected", path, phase, status: 403, reason: "authorizer-403" });
    return true;
  } catch {
    // API Gateway's own deny body is `{"Message": ...}` with no `type`, and a non-JSON body is
    // not something this platform produces -- either way it is not the CSRF problem.
    report({ step: "credential-rejected", path, phase, status: 403, reason: "unreadable-403" });
    return true;
  }
}

/**
 * Calls the platform API (`path` is a `/v1/...` route) with the session cookie.
 *
 * A `401` gets **exactly one** refresh-and-retry. That single retry is what lets a tab left open
 * past the access cookie's lifetime recover silently rather than falsely announcing an expired
 * session -- the common case by far, since the cookie is far shorter-lived than the session
 * behind it.
 *
 * A second failure is a genuinely dead session: the event fires and the caller gets a 401 body it
 * can render. Note the refresh route clears all three cookies itself in that case, so the
 * browser stops presenting a credential that will never work again.
 *
 * **`403` is retried too, and getting that wrong broke renewal completely.** This function used
 * to refresh on `401` only, on the reasoning that "with a cookie session a 403 means the
 * authorizer accepted the caller and the *route* refused them". That is backwards. The Lambda
 * authorizer denies by returning an IAM **Deny policy**, and API Gateway renders a Deny as
 * **403** -- `{"Message":"User is not authorized to access this resource with an explicit deny
 * in an identity-based policy"}`. Its own docstring says so explicitly, and says raising instead
 * would have produced 401.
 *
 * So an expired access cookie -- the single most common state for a returning user, since the
 * cookie lives about an hour and the session thirty days -- produced a 403 that this function
 * refused to retry. Renewal never fired for anyone. It presented as "returning to a session
 * gives 403s", which reads as a permissions problem rather than as an unrefreshed token.
 *
 * The one 403 that is genuinely ours is the CSRF guard, identified by `type:
 * "csrf-check-failed"`. Every other authorization failure in this platform is a **404** by
 * convention (CONVENTIONS.md: *"Authorization failures are 404, never 403"*, and
 * `admin.auth` raises `NotFoundProblem` rather than a 403 precisely so a non-admin cannot probe
 * for the existence of admin routes). That convention is what makes "any 403 that is not the
 * CSRF one came from the authorizer" a safe inference rather than a guess.
 *
 * Every branch below reports itself through `setAuthTelemetryReporter`'s reporter -- see
 * `AuthTelemetryEvent` for why an outside observer cannot reconstruct which one was taken.
 */
export async function platformFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await send(path, init);
  if (!(await credentialRejected(res, path, "initial"))) return res;

  if (!(await refreshSession(path))) {
    report({ step: "session-expired", path, after: "refresh-failed" });
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    return sessionExpiredResponse();
  }

  const retried = await send(path, init);
  if (await credentialRejected(retried, path, "retry")) {
    report({ step: "session-expired", path, after: "retry-rejected" });
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    return sessionExpiredResponse();
  }
  report({ step: "retry-recovered", path, status: retried.status });
  return retried;
}

/** Send the browser through the OIDC flow, returning here afterwards. */
export function startSignIn(next: string = window.location.pathname + window.location.search): void {
  // A full navigation, not a `fetch`. The flow is a chain of top-level redirects through Okta;
  // an XHR would follow them opaquely and land nowhere useful.
  window.location.assign(`${BASE}/v1/auth/login?next=${encodeURIComponent(next)}`);
}

/**
 * Erases every trace of the signed-out user that lives in the browser rather than in a cookie.
 *
 * **This is DESIGN.md §5.11, and it was simply missing.** The requirement is explicit -- *"logout
 * must clear caches, purge storage, and unregister the service worker -- in-store devices are
 * shared"* -- and the Next implementation it replaces was `lib/actions.ts:9`, a bare `signOut()`
 * and nothing else. The only `caches.delete` anywhere in the app was `sw.js`'s own stale-version
 * sweep, which runs on deploy and has nothing to do with users.
 *
 * The threat is not abstract for this product: a retail device is handed between employees across
 * a shift, so "the next person opens the browser" is the *expected* usage, not an edge case. What
 * survives a plain cookie logout is the service worker's cached HTML shell, whatever the consoles
 * left in `localStorage`, and `sessionStorage`.
 *
 * Order matters. The worker is unregistered **first**: it owns a `fetch` handler that writes to
 * the cache, so clearing caches while it is still installed leaves it free to refill them. It
 * does not detach until the last controlled page unloads, which the navigation immediately after
 * this call provides.
 *
 * Every step is independently guarded. `caches` and `navigator.serviceWorker` are absent outside a
 * secure context, `localStorage` throws outright when storage is disabled, and none of that is a
 * reason to leave the user signed in. A failure here must never become a failure to sign out.
 */
export async function purgeLocalState(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Deliberately swallowed -- see the note above on why no step here may block sign-out.
  }

  try {
    if ("caches" in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Deliberately swallowed.
  }

  try {
    window.localStorage.clear();
    window.sessionStorage.clear();
  } catch {
    // Deliberately swallowed. Both throw rather than no-op when storage is blocked.
  }

  // The TanStack Query cache is *not* cleared here, and that is not an omission: it lives in
  // memory only -- nothing persists it -- so the full navigation below discards it wholesale.
  // Clearing it explicitly would also repopulate it, because the components still mounted at that
  // moment would immediately refetch with a cookie that is already gone.
}

/** End the session server-side, purge the device, then return to the sign-in flow. */
export async function signOut(): Promise<void> {
  try {
    await fetch(`${BASE}/v1/auth/logout`, {
      method: "POST",
      credentials: "same-origin",
      headers: XHR_HEADER,
    });
  } catch {
    // A failed logout call still means the user asked to leave. The purge and the navigation both
    // have to happen regardless -- on a shared device, leaving cached content behind because the
    // network hiccuped is the worse outcome of the two.
  } finally {
    await purgeLocalState();
    // A full navigation, not a router push: it is what tears down the in-memory React tree, and
    // it is what finally detaches the service worker unregistered above.
    window.location.assign("/");
  }
}
